import { DatasetExploreItem } from "./dataset";
import {
  ECatchmentTransport,
  ERoadExclusions,
  ScoringOptionsEnum
} from "./spatial";

export interface GetAnalysisHistoryParams {
  page?: number;
  per_page?: number;
  query?: string;
  author_id?: string;
  created_at_from?: string;
  created_at_to?: string;
}

interface AnalysisHistoryDataset {
  id: string;
  name: string;
}

export type PolygonUI = {
  name: string;
  coordinates: string;
};

export interface PayloadAttributes {
  attributes?: DatasetAttribute[];
}

export interface AnalysisHistoryItem {
  id: string;
  created_at: string;
  author: string;
  analysis_name: string;
  datasets: AnalysisHistoryDataset[];
  input: string;
  output: string;
  expired_at: string;
  estimated_time_secs: number;
}

interface Pagination {
  current_page?: number;
  total_pages?: number;
  per_page: number;
  total: number;
  prev: number;
  next: number;
}

export type AnalysisHistoryListResponse = {
  data: AnalysisHistoryItem[];
  meta?: {
    pagination: Pagination;
  };
};

export enum OutputType {
  SITE_PROFILING = "site_profiling",
  H3 = "h3",
  GEOHASH = "geohash",
  GRID = "grid"
}

enum CatchmentMetric {
  DISTANCE = "distance",
  TIME = "time"
}

interface AnalysisOutput {
  level: number;
  output_type: OutputType;
  scoring_option: ScoringOptionsEnum;
}

interface AttributeFilter {
  criteria: string;
  include_nulls: boolean;
  type: string;
}

export interface DatasetAttribute {
  attribute_code: string;
  filter: AttributeFilter;
  id: string;
  name: string;
  weight_score: number;
}

interface CatchmentLocation {
  catchment_area: number | null;
  latitude: number;
  longitude: number;
  name: string;
}

interface Catchment {
  contours: number[];
  exclusions: ERoadExclusions[];
  locations: CatchmentLocation[];
  metric: CatchmentMetric;
  profile: ECatchmentTransport | "radius";
}

export interface AdmAreaInput {
  type: "adm_area";
  adm_area: string[][];
}

export interface CatchmentAreaInput {
  type: "catchment";
  catchment: Catchment;
}
export interface PolygonFeature {
  type: "Feature";
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: string | number[][][] | number[][][][];
  };
  properties: {
    name?: string;
    area_size?: number;
  };
}

export interface PolygonAreaInput {
  type: "custom_catchments";
  custom_catchments: {
    features: PolygonFeature[];
  };
}

export type AreaInput = AdmAreaInput | CatchmentAreaInput | PolygonAreaInput;

export interface DatasetHistoryAnalysisItem extends DatasetExploreItem {
  short_desc?: string;
  attributes?: DatasetAttribute[];
  region?: string;
  logo_asset_url?: string;
}

export interface AnalysisHistoryDetailResponse {
  analysis_output: AnalysisOutput;
  area_input: AreaInput;
  author_name: string;
  created_at: string;
  datasets: DatasetHistoryAnalysisItem[];
  expired_at: string;
  id: string;
}

export enum GeohashResolution {
  RES_1 = 1,
  RES_2 = 2,
  RES_3 = 3,
  RES_4 = 4,
  RES_5 = 5,
  RES_6 = 6
}

export const GEOHASH_RESOLUTION_LABEL: Record<GeohashResolution, string> = {
  [GeohashResolution.RES_1]: "≤ 5,000km x 5,000km",
  [GeohashResolution.RES_2]: "≤ 1,250km x 625km",
  [GeohashResolution.RES_3]: "≤ 156km x 156km",
  [GeohashResolution.RES_4]: "≤ 39.1km x 19.5km",
  [GeohashResolution.RES_5]: "≤ 4.89km x 4.89km",
  [GeohashResolution.RES_6]: "≤ 1.22km x 0.61km"
};

export enum H3Resolution {
  LEVEL_1 = 1,
  LEVEL_2 = 2,
  LEVEL_3 = 3,
  LEVEL_4 = 4,
  LEVEL_5 = 5,
  LEVEL_6 = 6,
  LEVEL_7 = 7,
  LEVEL_8 = 8,
  LEVEL_9 = 9,
  LEVEL_10 = 10
}

export const H3_RESOLUTION_META: Record<
  H3Resolution,
  { radiusKm: string; areaKm2: string }
> = {
  [H3Resolution.LEVEL_1]: { radiusKm: "483.05", areaKm2: "609,788.44" },
  [H3Resolution.LEVEL_2]: { radiusKm: "182.51", areaKm2: "86,801.78" },
  [H3Resolution.LEVEL_3]: { radiusKm: "68.97", areaKm2: "12,393.43" },
  [H3Resolution.LEVEL_4]: { radiusKm: "26.07", areaKm2: "1,770.34" },
  [H3Resolution.LEVEL_5]: { radiusKm: "9.85", areaKm2: "252.90" },
  [H3Resolution.LEVEL_6]: { radiusKm: "3.72", areaKm2: "36.12" },
  [H3Resolution.LEVEL_7]: { radiusKm: "1.40", areaKm2: "5.16" },
  [H3Resolution.LEVEL_8]: { radiusKm: "0.53", areaKm2: "0.73" },
  [H3Resolution.LEVEL_9]: { radiusKm: "0.20", areaKm2: "0.10" },
  [H3Resolution.LEVEL_10]: { radiusKm: "0.07", areaKm2: "0.01" }
};

export enum AnalysisHistoryInputType {
  CATCHMENT = "catchment",
  ADMIN = "adm_area",
  POLYGON = "custom_catchments"
}
